import { decodeFunctionResult, encodeFunctionData, keccak256, parseAbi, type Abi, type Address, type Hex } from "viem";
import { ELYSIUM_TRADE, sameTradeAddress } from "../config/trade";
import { nativeV3FactoryAbi, nativeV3PoolAbi, nativeV3RouterAbi } from "../contracts/native-v3";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { QUOTE_MULTICALL, readQuoteContracts, type ContractRead } from "./quote-contract-reads";

export type TradeObservation = { blockNumber: bigint; blockHash: Hex; observedAt: number };
const vaultAbi = parseAbi([
  "function sourceSwapPool(address token) view returns (address)",
  "function sourceSwapFactory() view returns (address)",
]);

export async function readTradeHead(provider: Eip1193Provider, guard: () => void,
  options: RpcReadOptions = {}): Promise<TradeObservation> {
  guard();
  const [chain, value] = await Promise.all([
    readRpc(provider, { method: "eth_chainId", params: [] }, options),
    readRpc(provider, { method: "eth_getBlockByNumber", params: ["latest", false] }, options),
  ]);
  guard();
  if (chain !== `0x${ELYSIUM_TRADE.chainId.toString(16)}`) throw new Error("ELYSIUM_CHAIN_MISMATCH");
  const head = value as { number?: string; hash?: Hex; timestamp?: string } | null;
  if (!head?.number || !/^0x[0-9a-f]+$/i.test(head.number) || !head.hash || !/^0x[0-9a-f]{64}$/i.test(head.hash) ||
    !head.timestamp || !/^0x[0-9a-f]+$/i.test(head.timestamp)) throw new Error("ELYSIUM_HEAD_INVALID");
  if (BigInt(head.number) < ELYSIUM_TRADE.deploymentBlock) throw new Error("ELYSIUM_HEAD_BEFORE_DEPLOYMENT");
  const age = BigInt(Math.floor(Date.now() / 1000)) - BigInt(head.timestamp);
  if (age < -30n || age > 120n) throw new Error("ELYSIUM_HEAD_STALE");
  return { blockNumber: BigInt(head.number), blockHash: head.hash, observedAt: performance.now() };
}

export async function confirmTradeHead(provider: Eip1193Provider, head: TradeObservation,
  guard: () => void, options: RpcReadOptions = {}): Promise<void> {
  guard();
  const confirmed = await readRpc(provider, { method: "eth_getBlockByNumber",
    params: [`0x${head.blockNumber.toString(16)}`, false] }, options) as { hash?: string } | null;
  guard();
  if (!sameTradeAddress(confirmed?.hash, head.blockHash)) throw new Error("ELYSIUM_HEAD_REORG");
}

/** Public quotes cover trading contracts; sends also verify the vault. Optional read-only
 * getters share the verified block/batch and are returned only after every binding passes. */
export async function checkTradeContracts(provider: Eip1193Provider, head: TradeObservation,
  guard: () => void, options: RpcReadOptions = {}, multicall = false, requireVault = false,
  additionalReads: readonly ContractRead[] = []): Promise<unknown[]> {
  const block = `0x${head.blockNumber.toString(16)}` as Hex;
  const addresses = [ELYSIUM_TRADE.token, ELYSIUM_TRADE.factory, ELYSIUM_TRADE.router, ELYSIUM_TRADE.pool,
    ...(multicall ? [QUOTE_MULTICALL.address] : []), ...(requireVault ? [ELYSIUM_TRADE.vault] : [])];
  guard();
  const code = await Promise.all(addresses.map(address => readRpc(provider,
    { method: "eth_getCode", params: [address, block] }, options)));
  guard();
  if (code.some(value => typeof value !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(value))) throw new Error("ELYSIUM_TRADE_CODE_MISSING");
  if (multicall && keccak256(code[4] as Hex) !== QUOTE_MULTICALL.codeHash) throw new Error("QUOTE_MULTICALL_CODE_MISMATCH");
  const calls: ContractRead[] = [
    { to: ELYSIUM_TRADE.factory, abi: nativeV3FactoryAbi, functionName: "getPool", args: [ELYSIUM_TRADE.token] },
    ...["token", "factory", "router"].map(functionName => ({ to: ELYSIUM_TRADE.pool, abi: nativeV3PoolAbi, functionName })),
    { to: ELYSIUM_TRADE.router, abi: nativeV3RouterAbi, functionName: "factory" },
    ...(requireVault ? [
      { to: ELYSIUM_TRADE.pool, abi: nativeV3PoolAbi, functionName: "feeVault" },
      { to: ELYSIUM_TRADE.vault, abi: vaultAbi, functionName: "sourceSwapPool", args: [ELYSIUM_TRADE.token] },
      { to: ELYSIUM_TRADE.vault, abi: vaultAbi, functionName: "sourceSwapFactory" },
    ] : []),
    ...additionalReads,
  ];
  const read = async (to: Address, abi: Abi, functionName: string, args: readonly unknown[] = []) => {
    guard();
    const raw = await readRpc(provider, { method: "eth_call", params: [{ to,
      data: encodeFunctionData({ abi, functionName, args }) }, block] }, options);
    guard();
    if (typeof raw !== "string") throw new Error("ELYSIUM_TRADE_BINDING_INVALID");
    return decodeFunctionResult({ abi, functionName, data: raw as Hex });
  };
  const bindings = multicall ? await readQuoteContracts(provider, calls, block, guard, options) :
    await Promise.all(calls.map(call => read(call.to, call.abi, call.functionName, call.args)));
  guard();
  const expected = [ELYSIUM_TRADE.pool, ELYSIUM_TRADE.token, ELYSIUM_TRADE.factory, ELYSIUM_TRADE.router, ELYSIUM_TRADE.factory,
    ...(requireVault ? [ELYSIUM_TRADE.vault, ELYSIUM_TRADE.pool, ELYSIUM_TRADE.factory] : [])];
  if (bindings.slice(0, expected.length).some((value, index) => typeof value !== "string" || !sameTradeAddress(value, expected[index]!)))
    throw new Error("ELYSIUM_TRADE_BINDING_MISMATCH");
  return bindings.slice(expected.length);
}

export async function observeTrade(provider: Eip1193Provider, guard: () => void,
  options: RpcReadOptions = {}, requireVault = false): Promise<TradeObservation> {
  const head = await readTradeHead(provider, guard, options);
  await checkTradeContracts(provider, head, guard, options, false, requireVault);
  await confirmTradeHead(provider, head, guard, options);
  return { ...head, observedAt: performance.now() };
}
